import {
  Bitmap,
  Color,
  ColorMatrix,
  ColorMatrixFilter,
  GlowFilter,
  MovieClip,
} from "../runtime";
import { SPR_sword, SPR_swordTrail, SPR_swordTrailBack } from "./assets";
import { SWORD_COLOURS, SWORD_HUES } from "./data/swordColours";
import { SpriteLayer, SwordGlowPulse } from "./SpriteLayer";

/** The part of an enemy's data bag that `slashHit` reads. */
export interface ThrownHitTarget {
  dead: boolean;
  xx: number;
  yy: number;
  hitRadius: number;
}

/** Ghost trails behind the blade; the first half lead, the rest lag on the back sprite. */
const TRAIL_TOTAL = 10;
const TRAIL_MAIN_TOTAL = 5;
/** Trail sheet frame used by the leading and lagging halves. */
const TRAIL_FRAME_MAIN = 3;
const TRAIL_FRAME_BACK = 1;
/** Frames before the blade stops coasting outward, and before it turns around. */
const COAST_UNTIL = 17;
const RETURN_AT = 36;

/** The player's weapon while it is in flight, thrown out and homing back. */
export class Thrown extends MovieClip {
  swordLevel: number;
  hitCD = 0;
  targetAngle: number;
  swordRadius: number;
  swishCounter = 0;
  private readonly tileSize = 64;
  private readonly spinSpeed = 20;
  private readonly glowPulse = new SwordGlowPulse();
  private readonly sword: MovieClip;
  private readonly swordBack: MovieClip;
  private readonly swordTrail: MovieClip[] = [];
  private readonly swordTrailData: number[] = [];
  private readonly trailLayer: SpriteLayer;
  private readonly trailBackLayer: SpriteLayer;
  private readonly swordMatrix: ColorMatrix;
  private brightnessSword = 0;
  private xSpeed: number;
  private ySpeed: number;
  private moveSpeed = 10;
  private returning = false;
  private life = 0;

  constructor(
    x: number,
    y: number,
    swordSizeX: number,
    swordSizeY: number,
    swordLevel: number,
    targetAngle: number,
  ) {
    super();
    this.x = x;
    this.y = y;
    this.targetAngle = targetAngle;
    this.swordLevel = swordLevel;
    this.swordRadius = 30 + this.swordLevel * 15;
    const rad = this.targetAngle * (Math.PI / 180);
    this.xSpeed = this.moveSpeed * Math.cos(rad);
    this.ySpeed = this.moveSpeed * Math.sin(rad);

    const trailSheet = new SPR_swordTrail();
    this.trailLayer = new SpriteLayer(trailSheet, this.tileSize);
    // the back trail deliberately uses the main trail's row width
    this.trailBackLayer = new SpriteLayer(
      new SPR_swordTrailBack(),
      this.tileSize,
      this.tileSize,
      trailSheet.width,
    );

    const swordTrailMain = new MovieClip();
    this.addChild(swordTrailMain);
    const swordTrailBack = new MovieClip();
    this.addChild(swordTrailBack);
    for (let trailIndex = 0; trailIndex < TRAIL_TOTAL; trailIndex++) {
      const isMain = trailIndex < TRAIL_MAIN_TOTAL;
      const swordTrailHolder = new MovieClip();
      const trailBitmap = new Bitmap(
        isMain ? this.trailLayer.canvas : this.trailBackLayer.canvas,
      );
      (isMain ? swordTrailMain : swordTrailBack).addChild(swordTrailHolder);
      trailBitmap.x -= trailBitmap.width * 0.03;
      trailBitmap.y -= trailBitmap.height * 0.5;
      swordTrailHolder.addChild(trailBitmap);
      swordTrailHolder.scaleX = swordSizeX;
      swordTrailHolder.scaleY = swordSizeY;
      this.swordTrailData.push(trailIndex * 10);
      this.swordTrail.push(swordTrailHolder);
    }

    this.sword = new MovieClip();
    this.sword.rotation = this.targetAngle;
    this.sword.scaleX = swordSizeX;
    this.sword.scaleY = swordSizeY;
    this.addChild(this.sword);
    this.swordBack = new MovieClip();
    this.swordBack.rotation = this.sword.rotation + 180;
    this.swordBack.scaleX = swordSizeX;
    this.swordBack.scaleY = swordSizeY;
    this.addChild(this.swordBack);

    const swordSheet = new SPR_sword();
    const swordLayer = new SpriteLayer(swordSheet, this.tileSize);
    const swordBackLayer = new SpriteLayer(swordSheet, this.tileSize);
    for (const [parent, layer] of [
      [this.sword, swordLayer],
      [this.swordBack, swordBackLayer],
    ] as const) {
      const bitmap = new Bitmap(layer.canvas);
      parent.addChild(bitmap);
      bitmap.x = this.tileSize * -0.03;
      bitmap.y = this.tileSize * -0.5;
      bitmap.smoothing = true;
    }
    // both faces always show sheet frame 1
    swordLayer.draw(1);
    swordBackLayer.draw(1);

    this.swordMatrix = new ColorMatrix();
    this.swordMatrix.adjustColor(0, 0, 0, SWORD_HUES[this.swordLevel]);
    this.filters = [new ColorMatrixFilter(this.swordMatrix)];
  }

  /** Returns 1 once the blade is back within catching range of the player. */
  update(playerX: number, playerY: number): number {
    let returnAction = 0;
    this.sword.rotation += this.spinSpeed;
    this.swordBack.rotation += this.spinSpeed;
    ++this.swishCounter;
    if (this.swishCounter === 10) {
      this.swishCounter = 0;
    }
    this.x += this.xSpeed;
    this.y += this.ySpeed;
    ++this.life;
    if (this.hitCD > 0) {
      --this.hitCD;
    }
    if (!this.returning) {
      this.updateOutbound();
    } else {
      returnAction = this.updateReturning(playerX, playerY);
    }
    this.updateGlow();
    this.updateTrail();
    return returnAction;
  }

  slashHit(enemyData: ThrownHitTarget): number {
    let enemyReturn = 0;
    if (!enemyData.dead) {
      const dx = enemyData.xx - this.x;
      const dy = enemyData.yy - this.y;
      if (
        Math.sqrt(dx * dx + dy * dy) <
        this.swordRadius + enemyData.hitRadius
      ) {
        this.hitCD = 9;
        this.brightnessSword = 120;
        this.updateGlow();
        enemyReturn = 1;
      }
    }
    return enemyReturn;
  }

  /** Coasts outward, easing the throw speed off before turning around. */
  private updateOutbound(): void {
    if (this.life >= COAST_UNTIL) {
      this.xSpeed += (0 - this.xSpeed) * 0.1;
      this.ySpeed += (0 - this.ySpeed) * 0.1;
    }
    if (this.life === RETURN_AT) {
      this.returning = true;
      this.moveSpeed = 0;
    }
  }

  /** Homes on the player, accelerating back up to full speed. */
  private updateReturning(playerX: number, playerY: number): number {
    this.targetAngle =
      Math.atan2(playerY - this.y, playerX - this.x) * (180 / Math.PI);
    const rad = this.targetAngle * (Math.PI / 180);
    this.xSpeed = this.moveSpeed * Math.cos(rad);
    this.ySpeed = this.moveSpeed * Math.sin(rad);
    if (this.moveSpeed < 10) {
      this.moveSpeed += 0.25;
    }
    const dx = this.x - playerX;
    const dy = this.y - playerY;
    return Math.sqrt(dx * dx + dy * dy) < 36 ? 1 : 0;
  }

  private updateGlow(): void {
    const glowCounterSword = this.glowPulse.advance();
    const gFinalSword = (glowCounterSword * 0.4 + 3) * (this.swordLevel * 1.5);
    const glowFilter = new GlowFilter(
      SWORD_COLOURS[this.swordLevel],
      1,
      gFinalSword,
      gFinalSword,
      2 + this.swordLevel * 0.5,
      1,
    );
    this.filters = [new ColorMatrixFilter(this.swordMatrix), glowFilter];
    if (this.brightnessSword > 0) {
      this.brightnessSword -= 20;
      const bSw = new Color();
      bSw.setTint(16777215, this.brightnessSword * 0.01);
      this.sword.transform.colorTransform = bSw;
      this.swordBack.transform.colorTransform = bSw;
    }
  }

  private updateTrail(): void {
    for (
      let trailIndex = 0;
      trailIndex < this.swordTrail.length;
      trailIndex++
    ) {
      const isMain = trailIndex < TRAIL_MAIN_TOTAL;
      const holder = this.swordTrail[trailIndex];
      this.swordTrailData[trailIndex] -= 50;
      holder.alpha = this.swordTrailData[trailIndex] * 0.01;
      if (this.swordTrailData[trailIndex] <= 0) {
        this.swordTrailData[trailIndex] += 100;
        holder.rotation = isMain
          ? this.sword.rotation
          : this.swordBack.rotation;
        holder.x = this.sword.x;
        holder.y = this.sword.y;
      }
      const frame = isMain ? TRAIL_FRAME_MAIN : TRAIL_FRAME_BACK;
      if (isMain) {
        this.trailLayer.draw(frame);
      } else {
        this.trailBackLayer.draw(frame);
      }
    }
  }
}
